import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import ServiceRollingPoster from './ServiceRollingPoster';
import { saveProjectRequest } from './storageService';
import { getActiveUser, registerUser, loginUser } from './authService';
import { formalizeProjectScope } from './aiService';

const WorkshopHome = ({ onOpenAuth }) => {
  const history = useHistory();

  // Multi-Deliverables Intake Configurator State
  const [selectedPlatforms, setSelectedPlatforms] = useState(['Responsive Web Application']);
  const [projectTitle, setProjectTitle] = useState('');
  const [projectVision, setProjectVision] = useState('');
  const [timelineTarget, setTimelineTarget] = useState('Standard (3 - 6 Weeks)');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPassword, setClientPassword] = useState('');
  const [clientOrg, setClientOrg] = useState('');
  const [intakeAuthMode, setIntakeAuthMode] = useState('signup'); // 'signup' | 'signin'
  const [intakeAuthError, setIntakeAuthError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAIEnhancing, setIsAIEnhancing] = useState(false);
  const [aiEnhancedBadge, setAiEnhancedBadge] = useState(false);
  const [copiedSpec, setCopiedSpec] = useState(false);
  const [previewDeliverable, setPreviewDeliverable] = useState('Responsive Web Application');
  const [rightPanelMode, setRightPanelMode] = useState('simulator'); // 'simulator' | 'roi'

  // Authenticated User State
  const [currentUser, setCurrentUser] = useState(null);

  // Flagship Shipped Ecosystem State
  const [activeAppModal, setActiveAppModal] = useState(null);
  const [ecosystemFilter, setEcosystemFilter] = useState('all');

  const flagshipApps = [
    {
      id: 'tape',
      name: 'Tape — Record',
      tagline: 'The Intelligence Behind Your Conversations',
      category: 'ai',
      badge: '● Live in App Store',
      badgeColor: '#059669',
      logo: '/assets/tape_appstore_official.jpg',
      route: 'https://apps.apple.com/us/app/tape-record/id6766102917',
      isExternal: true,
      summary: 'Executive AI-driven meeting documentation & audio intelligence suite. Features offline-first Apple Watch recording, one-tap dynamic translation across 20+ languages, automated MoM & executive summaries, biometric security, and RevenueCat subscription monetization.',
      metrics: [
        { label: 'Platform', value: 'iOS & watchOS' },
        { label: 'Translation', value: '20+ Languages' },
        { label: 'Security', value: 'FaceID Biometrics' }
      ],
      stack: ['Swift / Capacitor', 'Apple WatchKit', 'Whisper / Gemini AI', 'RevenueCat', 'Apple Auth', 'Firebase'],
      architectureDetails: {
        topology: 'Apple Watch & iPhone Native Audio Stream → Biometric Guard → Ephemeral Whisper/Gemini Pipeline → Multi-Language MoM Translation & Export',
        cloudCost: '$0.00/mo Idle (Serverless Audio Inference)',
        security: 'Apple FaceID/TouchID biometric enclave, zero-retention ephemeral speech processing, encrypted cloud storage.',
        keyInnovations: [
          'Offline-first watchOS recording with automatic iPhone synchronization.',
          'One-tap dynamic translation of transcripts and summaries into 20+ languages.',
          'Integrated in-app purchases and tier entitlement via RevenueCat SDK.'
        ]
      },
      intakePreset: {
        platforms: ['Responsive Web Application', 'Native iOS Application', 'Serverless Cloud & APIs'],
        title: 'Cross-Platform Media Recording & Audio Intelligence Suite',
        vision: 'Build a native iOS/Web audio intelligence application with biometric authentication, Apple Sign-In, and subscription billing.'
      }
    },
    {
      id: 'vaak',
      name: 'Vaak',
      tagline: 'Immersive AI Language Learning & Speech Engine',
      category: 'ai',
      badge: '● Live in App Store',
      badgeColor: '#059669',
      logo: '/assets/vaak_appstore_official.jpg',
      route: 'https://apps.apple.com/us/app/vaak/id6760237875',
      isExternal: true,
      summary: 'Interactive speech-first language learning engine featuring the conversational "Scenario Dojo", deep AI pronunciation feedback, AWS Polly neural voice synthesis, and interactive 60fps Rive mascot animations.',
      metrics: [
        { label: 'App Store', value: 'Live on iOS' },
        { label: 'Speech Engine', value: 'AWS Polly Neural' },
        { label: 'Animations', value: 'Rive 60fps' }
      ],
      stack: ['AWS Polly', 'Google Gemini AI', 'Rive Canvas', 'Speech Recognition', 'Capacitor iOS', 'Tailwind'],
      architectureDetails: {
        topology: 'Microphone Stream → Native Speech Recognition → Google Gemini Evaluator → AWS Polly Neural Audio → Rive Animation Canvas',
        cloudCost: '$0.00/mo Idle (Pay-per-utterance AWS Polly & Gemini free tiers)',
        security: 'Client-side audio tokenization, ephemeral pronunciation scoring, secure Firebase Auth.',
        keyInnovations: [
          'Conversational Scenario Dojo with real-time accent and pronunciation scoring.',
          'Gamified interactive lesson loops with dynamic Rive canvas animations.',
          'High-fidelity multi-dialect pronunciation synthesis via AWS Polly neural voices.'
        ]
      },
      intakePreset: {
        platforms: ['Responsive Web Application', 'Native iOS Application', 'Serverless Cloud & APIs'],
        title: 'Interactive AI Language Learning Platform & Speech Engine',
        vision: 'Develop a gamified language learning application with AWS Polly neural voice synthesis, Rive animations, and Gemini AI conversational grading.'
      }
    },
    {
      id: 'mathguru',
      name: 'Math Guru',
      tagline: '3D Spatial STEM & Gamified Math Journey',
      category: 'stem',
      badge: '● Live in App Store',
      badgeColor: '#059669',
      logo: '/assets/math_appstore_official.jpg',
      route: 'https://apps.apple.com/us/app/math-guru-kids-math-gamified/id6757752035',
      isExternal: true,
      summary: 'Space-themed interactive STEM adventure for grades K-5 featuring an adaptive Sensei Algorithm, smart QR worksheet optical scanning, Three.js 3D WebGL simulations, and Gemini AI step-by-step problem evaluation.',
      metrics: [
        { label: 'App Store', value: '5.0 ★ Rated' },
        { label: 'Adaptive Engine', value: 'Sensei Algorithm' },
        { label: 'Computer Vision', value: 'QR Worksheet Scan' }
      ],
      stack: ['Three.js (Fiber/Drei)', 'Google Gemini AI', 'Camera QR Scanner', 'Rubiks Solver', 'Recharts', 'Capacitor'],
      architectureDetails: {
        topology: 'Camera / Mathematical Input → Three.js 3D WebGL Canvas ⇄ Client Solver Algorithms ⇄ Gemini AI Step-by-Step Reasoner',
        cloudCost: '$0.00/mo Idle (100% Client-Side WebGL & On-Demand AI)',
        security: 'Local camera stream isolation, sandbox canvas computation, zero image server storage.',
        keyInnovations: [
          'Real-time 3D interactive Rubik’s cube manipulation and optimal algorithmic solver.',
          'Camera optical scanner integration for dynamic formula detection and print export.',
          'Multi-platform packaging across iOS and Android with unified React 19 codebase.'
        ]
      },
      intakePreset: {
        platforms: ['Responsive Web Application', 'Native iOS Application'],
        title: '3D Spatial STEM Simulation & Interactive Math Engine',
        vision: 'Commission a 3D WebGL interactive learning platform with Three.js graphics, camera scanning, and generative AI problem solving.'
      }
    },
    {
      id: 'savanna',
      name: 'Savanna United',
      tagline: 'HOA Community Governance & Proxy Voting OS',
      category: 'saas',
      badge: '● Live in Production',
      badgeColor: '#059669',
      logo: '/assets/savanna.png',
      summary: 'Enterprise civic & HOA community management platform built for 1,000+ homeowners. Features verified homeowner registration, certified proxy voting, Leaflet GIS parcel mapping, and a Groq AI-powered Board Suggestion Box.',
      metrics: [
        { label: 'Civic Scale', value: '1,000+ Homes' },
        { label: 'Mapping', value: 'Leaflet GIS Parcels' },
        { label: 'AI Suggestion', value: 'Groq AI Board Box' }
      ],
      stack: ['Python Flask', 'Leaflet.js GIS', 'Groq AI', 'AWS S3', 'Elastic Beanstalk', 'SendGrid', 'Tailwind'],
      architectureDetails: {
        topology: 'Verified Homeowner Portal → Leaflet GIS Parcel Map → Groq AI Suggestion Processor → Certified Election & Proxy Voting Ledger',
        cloudCost: '$0.00/mo Idle (AWS Serverless & Edge Storage)',
        security: 'Strict address parcel verification, cryptographic proxy vote confirmation, role-based Board/Superadmin governance.',
        keyInnovations: [
          'Full election lifecycle engine with proxy collector assignment and verifiable vote audits.',
          'Interactive GIS neighborhood parcel map displaying homeowner verification status and campaign coverage.',
          'Groq AI-powered Board Suggestion Box analyzing sentiment, categorizing proposals, and drafting board responses.',
          'Bilingual automated homeowner outreach (English & Spanish) with transactional email broadcast.'
        ]
      },
      intakePreset: {
        platforms: ['Responsive Web Application', 'Serverless Cloud & APIs', '24/7 SLA Maintenance Retainer'],
        title: 'HOA Community Governance & Certified Proxy Voting Platform',
        vision: 'Build a civic community management portal with Leaflet GIS mapping, certified election proxy voting, and AI suggestion moderation.'
      }
    },
    {
      id: 'geeta',
      name: 'Gita — Srimad Bhagavad Gita',
      tagline: 'Digital Sanskrit Recitation & Dynamic Audio Suite',
      category: 'stem',
      badge: '● Live in Production',
      badgeColor: '#059669',
      logo: '/assets/geeta_app_icon.jpg',
      route: 'https://vishwaprabodhan.com/',
      isExternal: true,
      summary: 'Comprehensive multi-lingual spiritual recitation platform featuring original Sanskrit shlokas, multi-language translations, AWS Polly audio synthesis, and real-time verse search.',
      metrics: [
        { label: 'Content', value: '700 Verses' },
        { label: 'Audio Engine', value: 'AWS Polly / S3' },
        { label: 'Database', value: 'AWS DynamoDB' }
      ],
      stack: ['AWS Polly', 'AWS DynamoDB', 'Supabase', 'Capacitor Mobile', 'React', 'Tailwind'],
      architectureDetails: {
        topology: 'Client Portal (Web/PWA) → AWS DynamoDB Verse Cache → AWS Polly Audio Stream → Low-Latency Indexed Search',
        cloudCost: '$0.00/mo Idle (AWS DynamoDB On-Demand Free Tier)',
        security: 'Edge caching, immutable scripture content distribution, zero tracking footprint.',
        keyInnovations: [
          'High-speed phonetic verse search across Sanskrit, Hindi, Marathi, and English.',
          'Dynamic synchronized audio recitation pipeline leveraging AWS Polly neural speech.',
          'Offline-first PWA caching for mobile recitation without cellular connectivity.'
        ]
      },
      intakePreset: {
        platforms: ['Responsive Web Application', 'Native iOS Application', 'Serverless Cloud & APIs'],
        title: 'High-Throughput Digital Audio & Archive Portal',
        vision: 'Build a digital publishing and audio recitation portal with multilingual search, AWS Polly speech synthesis, and offline caching.'
      }
    }
  ];

  const handleCommissionApp = (app) => {
    setSelectedPlatforms(app.intakePreset.platforms);
    setProjectTitle(app.intakePreset.title);
    setProjectVision(app.intakePreset.vision);
    setActiveAppModal(null);
    const intakeEl = document.getElementById('intake');
    if (intakeEl) {
      intakeEl.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => {
        const inputEl = document.getElementById('intake-project-title');
        if (inputEl) {
          inputEl.focus();
          inputEl.style.boxShadow = '0 0 0 3px rgba(29, 78, 216, 0.35)';
          setTimeout(() => { inputEl.style.boxShadow = 'none'; }, 1800);
        }
      }, 500);
    }
  };

  const togglePlatform = (plat) => {
    setPreviewDeliverable(plat);
    if (selectedPlatforms.includes(plat)) {
      if (selectedPlatforms.length > 1) {
        setSelectedPlatforms(selectedPlatforms.filter(p => p !== plat));
      }
    } else {
      setSelectedPlatforms([...selectedPlatforms, plat]);
    }
  };

  useEffect(() => {
    const checkUser = () => {
      const user = getActiveUser();
      setCurrentUser(user);
      if (user) {
        setClientName(user.name || '');
        setClientEmail(user.email || '');
      }
    };
    checkUser();
    window.addEventListener('auth_state_changed', checkUser);
    return () => {
      window.removeEventListener('auth_state_changed', checkUser);
    };
  }, []);

  const handleHeroPromptSubmit = (e) => {
    e.preventDefault();
    const intakeEl = document.getElementById('intake');
    if (intakeEl) {
      intakeEl.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => {
        const titleEl = document.getElementById('intake-project-title');
        if (titleEl) {
          titleEl.focus();
          titleEl.style.boxShadow = '0 0 0 3px rgba(29, 78, 216, 0.35)';
          setTimeout(() => { titleEl.style.boxShadow = 'none'; }, 1800);
        }
      }, 500);
    }
  };

  const handleAIEnhanceVision = async () => {
    setIsAIEnhancing(true);
    try {
      const res = await formalizeProjectScope(projectVision, selectedPlatforms);
      if (res) {
        setProjectVision(res.formalizedVision);
        if (!projectTitle.trim() || projectTitle.includes('Architecture') || projectTitle.includes('Project')) {
          setProjectTitle(res.suggestedTitle);
        }
        setAiEnhancedBadge(true);
        setTimeout(() => setAiEnhancedBadge(false), 4500);
      }
    } catch (err) {
      console.error('AI enhance error', err);
    } finally {
      setIsAIEnhancing(false);
    }
  };

  const handleCopySpec = () => {
    if (projectVision && navigator.clipboard) {
      navigator.clipboard.writeText(projectVision);
      setCopiedSpec(true);
      setTimeout(() => setCopiedSpec(false), 2500);
    }
  };

  const handleFinalIntakeSubmit = async (e) => {
    e.preventDefault();
    setIntakeAuthError('');
    setIsSubmitting(true);

    let activeUser = currentUser;

    // Strict Account Authentication Gate: User MUST be signed in or registered so RFP is permanently tied to account
    if (!activeUser) {
      const email = clientEmail.trim();
      const password = clientPassword.trim();

      if (!email || !password) {
        setIntakeAuthError('Please provide your work email and an account password so your project is secured to your profile.');
        setIsSubmitting(false);
        return;
      }

      if (intakeAuthMode === 'signin') {
        const loginRes = await loginUser(email, password);
        if (!loginRes.success) {
          setIntakeAuthError(loginRes.error || 'Failed to sign in. Please verify your email and password.');
          setIsSubmitting(false);
          return;
        }
        activeUser = loginRes.user;
        setCurrentUser(activeUser);
      } else {
        const name = clientName.trim();
        if (!name) {
          setIntakeAuthError('Please enter your full name to create your client account.');
          setIsSubmitting(false);
          return;
        }
        const regRes = await registerUser({
          email,
          password,
          name,
          organization: clientOrg.trim() || 'Client Project'
        });
        if (!regRes.success) {
          if (regRes.error && regRes.error.toLowerCase().includes('already')) {
            setIntakeAuthError('An account with this email already exists. Switch to "Sign In" to submit with your existing credentials.');
            setIntakeAuthMode('signin');
            setIsSubmitting(false);
            return;
          }
          setIntakeAuthError(regRes.error || 'Could not register account. Please check your details.');
          setIsSubmitting(false);
          return;
        }
        activeUser = regRes.user;
        setCurrentUser(activeUser);
      }
    }

    const deliverablesSummary = selectedPlatforms.join(' + ');

    const newProject = saveProjectRequest({
      category: deliverablesSummary,
      platforms: selectedPlatforms,
      title: projectTitle.trim() || `${deliverablesSummary} Architecture`,
      description: projectVision.trim() || 'Bespoke software application with secure auth and cloud backend.',
      timelineTarget,
      name: activeUser.name,
      email: activeUser.email,
      organization: activeUser.organization || clientOrg.trim() || 'Client Project'
    });

    if (newProject) {
      history.push(`/project/${newProject.id}`);
    }
  };

  return (
    <div style={{ background: 'transparent', color: '#0f172a', minHeight: '100vh', overflowX: 'hidden', position: 'relative' }}>
      {/* 1. Executive Light Hero Section (Stripe / Apple / Linear Caliber) */}
      <section style={{
        position: 'relative',
        paddingTop: '5rem',
        paddingBottom: '5.5rem',
        background: 'transparent',
        borderBottom: '1px solid #e2e8f0',
        overflow: 'hidden'
      }}>
        <div className="container-custom" style={{ position: 'relative', zIndex: 2 }}>
          {/* Top Pill Status */}
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.6rem',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              padding: '0.4rem 1.1rem',
              borderRadius: '9999px',
              color: '#1d4ed8',
              fontSize: '0.82rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#059669', boxShadow: '0 0 8px rgba(5, 150, 105, 0.4)', flexShrink: 0 }}></div>
              <span className="d-none d-sm-inline">GURU&apos;S WORKSHOP LLC &bull; </span>
              <span>PRODUCTION SOFTWARE &bull; UPTIME SLAS AVAILABLE</span>
            </div>
          </div>

          {/* Commanding Hero Headline */}
          <div style={{ textAlign: 'center', maxWidth: '880px', margin: '0 auto 1.8rem auto' }}>
            <h1 style={{
              fontSize: 'clamp(1.9rem, 6.2vw, 4.4rem)',
              lineHeight: '1.15',
              fontWeight: 900,
              letterSpacing: '-0.035em',
              color: '#0f172a',
              margin: '0 auto 1.2rem auto'
            }}>
              We Build Software That Scales.<br />
              <span style={{
                background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 50%, #1e40af 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                From Architecture to Production.
              </span>
            </h1>
            <p style={{
              fontSize: 'clamp(1rem, 2.8vw, 1.18rem)',
              color: '#475569',
              lineHeight: '1.65',
              maxWidth: '680px',
              margin: '0 auto 2.5rem auto'
            }}>
              Bespoke web applications, cross-platform mobile platforms, and zero-idle-cost cloud architectures. Built with permanent <strong>$0/month baseline economics</strong> (AWS/GCP free-tier limits apply), 3-stage milestone protection, and full source code handover.
            </p>

            {/* Quick Interactive Prompt Bar */}
            <form onSubmit={handleHeroPromptSubmit} className="hero-prompt-form">
              <input
                type="text"
                placeholder="What do you want to build? (e.g., Client Portal, iOS Utility, AWS Migration)..."
                value={projectVision}
                onChange={(e) => setProjectVision(e.target.value)}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: '#0f172a',
                  fontSize: '1rem',
                  padding: '0.75rem 1rem'
                }}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary-blue"
                style={{
                  backgroundColor: '#1d4ed8',
                  color: '#ffffff',
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.92rem',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  borderRadius: '10px'
                }}
              >
                Get Scope Spec →
              </button>
            </form>

            {/* Logged-out Visitor Quick Client Login CTA */}
            {!currentUser && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '0.6rem',
                margin: '0.9rem auto 0 auto',
                flexWrap: 'wrap'
              }}>
                <span style={{ fontSize: '0.84rem', color: '#64748b' }}>
                  Have an active project or statement of work?
                </span>
                <button
                  type="button"
                  onClick={() => {
                    if (onOpenAuth) {
                      onOpenAuth('login');
                    } else {
                      window.dispatchEvent(new CustomEvent('open_auth_modal', { detail: 'login' }));
                    }
                  }}
                  style={{
                    background: '#ffffff',
                    border: '1.5px solid #1d4ed8',
                    color: '#1d4ed8',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    padding: '0.28rem 0.85rem',
                    borderRadius: '999px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 6px rgba(29, 78, 216, 0.08)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.35rem'
                  }}
                >
                  <span>🔐</span> Client Login
                </button>
              </div>
            )}

            {/* 1-Click Fast-Track Entry Archetypes (No One Has to Hunt For It) */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.6rem',
              flexWrap: 'wrap',
              margin: '0 auto 1.8rem auto',
              maxWidth: '850px'
            }}>
              <span style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>Fast-Track Launch:</span>
              {[
                { label: '💻 B2B Web App', platforms: ['Responsive Web Application'], title: 'Enterprise Web Application Portal' },
                { label: '📱 iOS & Android Suite', platforms: ['Native iOS Application', 'Native Android Application'], title: 'Cross-Platform Mobile Application' },
                { label: '⚡ Cloud & Database', platforms: ['Serverless Cloud & APIs'], title: 'Scalable Cloud Backend & Database' },
                { label: '🚀 Full-Stack Product', platforms: ['Responsive Web Application', 'Native iOS Application', 'Serverless Cloud & APIs', '24/7 SLA Maintenance Retainer'], title: 'Complete Multi-Platform Product Suite' }
              ].map(arch => (
                <button
                  key={arch.label}
                  type="button"
                  onClick={() => {
                    setSelectedPlatforms(arch.platforms);
                    setProjectTitle(arch.title);
                    const intakeEl = document.getElementById('intake');
                    if (intakeEl) intakeEl.scrollIntoView({ behavior: 'smooth' });
                  }}
                  style={{
                    background: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '999px',
                    padding: '0.35rem 0.85rem',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: '#0f172a',
                    cursor: 'pointer',
                    boxShadow: '0 2px 5px rgba(15, 23, 42, 0.04)',
                    transition: 'all 0.2s ease',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3rem'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#1d4ed8'; e.currentTarget.style.color = '#1d4ed8'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#0f172a'; }}
                >
                  {arch.label}
                </button>
              ))}
            </div>

            {/* Quick Trust Pills */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1.8rem', flexWrap: 'wrap', fontSize: '0.86rem', color: '#475569' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <strong style={{ color: '#059669' }}>✓</strong> $0 Upfront Architecture Scope
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <strong style={{ color: '#059669' }}>✓</strong> 3-Stage Milestone Payments
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <strong style={{ color: '#059669' }}>✓</strong> Client Code &amp; IP Handover
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <strong style={{ color: '#059669' }}>✓</strong> Mutual NDA Protected
              </span>
            </div>
          </div>

          {/* Dual-State Hero Showcase: Authenticated Command Bar vs Production Shipped Apps */}
          {currentUser ? (
            <div style={{
              maxWidth: '1020px',
              margin: '3rem auto 0 auto',
              background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)',
              borderRadius: '20px',
              border: '1.5px solid #bfdbfe',
              padding: '2rem 2.5rem',
              boxShadow: '0 20px 45px -10px rgba(29, 78, 216, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '1.5rem'
            }}>
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#dbeafe', color: '#1d4ed8', fontSize: '0.78rem', fontWeight: 800, padding: '0.25rem 0.75rem', borderRadius: '999px', marginBottom: '0.6rem' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#059669' }}></span>
                  CLIENT ACCOUNT VERIFIED
                </div>
                <h3 style={{ margin: '0 0 0.3rem 0', fontSize: '1.5rem', fontWeight: 900, color: '#0f172a' }}>
                  Welcome back, {currentUser.name}!
                </h3>
                <p style={{ margin: 0, color: '#475569', fontSize: '0.92rem' }}>
                  Logged in as <strong>{currentUser.email}</strong>. Track your proposals, review milestone deliveries, or submit a new scope request.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
                <Link
                  to="/dashboard"
                  className="btn-primary-blue"
                  style={{ padding: '0.75rem 1.4rem', fontSize: '0.92rem', borderRadius: '10px', textDecoration: 'none' }}
                >
                  Open Client Dashboard →
                </Link>
                <Link
                  to="/track"
                  style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #cbd5e1',
                    color: '#0f172a',
                    padding: '0.75rem 1.2rem',
                    fontSize: '0.92rem',
                    fontWeight: 700,
                    borderRadius: '10px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                >
                  Track Project Code
                </Link>
              </div>
            </div>
          ) : (
            <div style={{
              maxWidth: '1060px',
              margin: '3rem auto 0 auto',
              background: '#ffffff',
              borderRadius: '24px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 24px 60px -15px rgba(15, 23, 42, 0.08)',
              overflow: 'hidden'
            }}>
              {/* Window Header Bar */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.9rem 1.5rem',
                borderBottom: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
                flexWrap: 'wrap',
                gap: '0.6rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#ef4444' }}></div>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#f59e0b' }}></div>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#10b981' }}></div>
                  <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#1d4ed8', letterSpacing: '0.04em', marginLeft: '0.4rem' }}>
                    PROVEN TRACK RECORD &bull; FLAGSHIP PRODUCTION SYSTEMS
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
                  <span style={{ backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', color: '#059669', fontSize: '0.74rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: '999px' }}>
                    ● Live in App Store &amp; Cloud
                  </span>
                </div>
              </div>

              {/* Shipped Systems Cards */}
              <div style={{ padding: '1.8rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: '1.2rem' }}>
                {/* Tape Card */}
                <div style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '16px',
                  padding: '1.4rem',
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.8rem' }}>
                      <img src="/assets/tape_appstore_official.jpg" alt="Tape" style={{ width: '48px', height: '48px', borderRadius: '12px', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                      <div>
                        <h4 style={{ margin: 0, fontSize: '1.08rem', fontWeight: 800, color: '#0f172a' }}>Tape — Record</h4>
                        <div style={{ fontSize: '0.74rem', color: '#059669', fontWeight: 700 }}>● Live on Apple App Store</div>
                      </div>
                    </div>
                    <p style={{ fontSize: '0.82rem', color: '#475569', lineHeight: '1.5', margin: '0 0 1rem 0' }}>
                      AI audio intelligence &amp; meeting documentation for iPhone &amp; Apple Watch. Biometrics, multi-language translation, and zero-idle cloud inference.
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '1.2rem' }}>
                      {['Swift / iOS', 'WatchKit', 'Whisper AI', 'RevenueCat'].map(t => (
                        <span key={t} style={{ backgroundColor: '#f1f5f9', color: '#334155', fontSize: '0.72rem', fontWeight: 600, padding: '0.15rem 0.45rem', borderRadius: '4px' }}>{t}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.6rem' }}>
                    <a
                      href="https://apps.apple.com/us/app/tape-record/id6766102917"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        flex: 1,
                        textAlign: 'center',
                        backgroundColor: '#0f172a',
                        color: '#ffffff',
                        padding: '0.55rem',
                        borderRadius: '8px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        textDecoration: 'none'
                      }}
                    >
                       App Store →
                    </a>
                    <button
                      type="button"
                      onClick={() => handleCommissionApp(flagshipApps.find(a => a.id === 'tape'))}
                      style={{
                        backgroundColor: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        color: '#1d4ed8',
                        padding: '0.55rem 0.8rem',
                        borderRadius: '8px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      Build Similar
                    </button>
                  </div>
                </div>

                {/* Vaak Card */}
                <div style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '16px',
                  padding: '1.4rem',
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.8rem' }}>
                      <img src="/assets/vaak_appstore_official.jpg" alt="Vaak" style={{ width: '48px', height: '48px', borderRadius: '12px', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                      <div>
                        <h4 style={{ margin: 0, fontSize: '1.08rem', fontWeight: 800, color: '#0f172a' }}>Vaak</h4>
                        <div style={{ fontSize: '0.74rem', color: '#059669', fontWeight: 700 }}>● Live on Apple App Store</div>
                      </div>
                    </div>
                    <p style={{ fontSize: '0.82rem', color: '#475569', lineHeight: '1.5', margin: '0 0 1rem 0' }}>
                      Speech-first language engine with conversational Scenario Dojo, AWS Polly neural audio synthesis, and 60fps interactive Rive mascot animations.
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '1.2rem' }}>
                      {['AWS Polly', 'Google Gemini AI', 'Rive 60fps', 'Capacitor iOS'].map(t => (
                        <span key={t} style={{ backgroundColor: '#f1f5f9', color: '#334155', fontSize: '0.72rem', fontWeight: 600, padding: '0.15rem 0.45rem', borderRadius: '4px' }}>{t}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.6rem' }}>
                    <a
                      href="https://apps.apple.com/us/app/vaak/id6760237875"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        flex: 1,
                        textAlign: 'center',
                        backgroundColor: '#0f172a',
                        color: '#ffffff',
                        padding: '0.55rem',
                        borderRadius: '8px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        textDecoration: 'none'
                      }}
                    >
                       App Store →
                    </a>
                    <button
                      type="button"
                      onClick={() => handleCommissionApp(flagshipApps.find(a => a.id === 'vaak'))}
                      style={{
                        backgroundColor: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        color: '#1d4ed8',
                        padding: '0.55rem 0.8rem',
                        borderRadius: '8px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      Build Similar
                    </button>
                  </div>
                </div>

                {/* Gita Card */}
                <div style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '16px',
                  padding: '1.4rem',
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.8rem' }}>
                      <img src="/assets/geeta_app_icon.jpg" alt="Gita" style={{ width: '48px', height: '48px', borderRadius: '12px', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                      <div>
                        <h4 style={{ margin: 0, fontSize: '1.08rem', fontWeight: 800, color: '#0f172a' }}>Gita &bull; Sanskrit Suite</h4>
                        <div style={{ fontSize: '0.74rem', color: '#059669', fontWeight: 700 }}>● Live Web App &bull; PWA</div>
                      </div>
                    </div>
                    <p style={{ fontSize: '0.82rem', color: '#475569', lineHeight: '1.5', margin: '0 0 1rem 0' }}>
                      Multi-lingual spiritual recitation engine with 700 verses, AWS DynamoDB indexing, AWS Polly audio synthesis, and low-latency search.
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '1.2rem' }}>
                      {['AWS DynamoDB', 'AWS Polly', 'React PWA', '$0/mo Idle'].map(t => (
                        <span key={t} style={{ backgroundColor: '#f1f5f9', color: '#334155', fontSize: '0.72rem', fontWeight: 600, padding: '0.15rem 0.45rem', borderRadius: '4px' }}>{t}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.6rem' }}>
                    <a
                      href="https://vishwaprabodhan.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        flex: 1,
                        textAlign: 'center',
                        backgroundColor: '#1d4ed8',
                        color: '#ffffff',
                        padding: '0.55rem',
                        borderRadius: '8px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        textDecoration: 'none'
                      }}
                    >
                      Visit Live Web App →
                    </a>
                    <button
                      type="button"
                      onClick={() => handleCommissionApp(flagshipApps.find(a => a.id === 'geeta'))}
                      style={{
                        backgroundColor: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        color: '#1d4ed8',
                        padding: '0.55rem 0.8rem',
                        borderRadius: '8px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      Build Similar
                    </button>
                  </div>
                </div>

                {/* Math Guru Card */}
                <div style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '16px',
                  padding: '1.4rem',
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.8rem' }}>
                      <img src="/assets/math_appstore_official.jpg" alt="Math Guru" style={{ width: '48px', height: '48px', borderRadius: '12px', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                      <div>
                        <h4 style={{ margin: 0, fontSize: '1.08rem', fontWeight: 800, color: '#0f172a' }}>Math Guru &bull; STEM</h4>
                        <div style={{ fontSize: '0.74rem', color: '#059669', fontWeight: 700 }}>● 5.0 ★ Rated &bull; Apple App Store</div>
                      </div>
                    </div>
                    <p style={{ fontSize: '0.82rem', color: '#475569', lineHeight: '1.5', margin: '0 0 1rem 0' }}>
                      3D spatial STEM adventure with adaptive Sensei Algorithm, smart QR worksheet optical scanning, Three.js WebGL physics, and Gemini AI problem solving.
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '1.2rem' }}>
                      {['Three.js 3D', 'Computer Vision', 'Gemini AI', 'Capacitor iOS'].map(t => (
                        <span key={t} style={{ backgroundColor: '#f1f5f9', color: '#334155', fontSize: '0.72rem', fontWeight: 600, padding: '0.15rem 0.45rem', borderRadius: '4px' }}>{t}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.6rem' }}>
                    <a
                      href="https://apps.apple.com/us/app/math-guru-kids-math-gamified/id6757752035"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        flex: 1,
                        textAlign: 'center',
                        backgroundColor: '#0f172a',
                        color: '#ffffff',
                        padding: '0.55rem',
                        borderRadius: '8px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        textDecoration: 'none'
                      }}
                    >
                       App Store →
                    </a>
                    <button
                      type="button"
                      onClick={() => handleCommissionApp(flagshipApps.find(a => a.id === 'mathguru'))}
                      style={{
                        backgroundColor: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        color: '#1d4ed8',
                        padding: '0.55rem 0.8rem',
                        borderRadius: '8px',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      Build Similar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 2. Executive Metrics KPI Bar (Executive Light Palette) */}
      <section style={{
        backgroundColor: 'rgba(248, 250, 252, 0.72)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid #e2e8f0',
        padding: '3rem 0'
      }}>
        <div className="container-custom">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '2rem'
          }}>
            <div>
              <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '2.5rem', fontWeight: 900, color: '#1d4ed8', lineHeight: 1 }}>
                0%
              </div>
              <div style={{ color: '#0f172a', fontWeight: 700, fontSize: '0.95rem', margin: '0.4rem 0 0.2rem 0' }}>
                SaaS Tax Markup
              </div>
              <div style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: 1.4 }}>
                Pure native code &amp; permanent $0/month baseline cloud infrastructure (AWS/GCP free tiers apply).
              </div>
            </div>

            <div>
              <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '2.5rem', fontWeight: 900, color: '#059669', lineHeight: 1 }}>
                &lt; 24h
              </div>
              <div style={{ color: '#0f172a', fontWeight: 700, fontSize: '0.95rem', margin: '0.4rem 0 0.2rem 0' }}>
                SOW Turnaround Target
              </div>
              <div style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: 1.4 }}>
                Itemized deliverables, timeline, and staged milestone payment breakdown.
              </div>
            </div>

            <div>
              <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '2.5rem', fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>
                100%
              </div>
              <div style={{ color: '#0f172a', fontWeight: 700, fontSize: '0.95rem', margin: '0.4rem 0 0.2rem 0' }}>
                IP &amp; Code Ownership
              </div>
              <div style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: 1.4 }}>
                Full repository handover per contract with zero vendor lock-in or proprietary fees.
              </div>
            </div>

            <div>
              <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: '2.5rem', fontWeight: 900, color: '#d97706', lineHeight: 1 }}>
                3-Stage
              </div>
              <div style={{ color: '#0f172a', fontWeight: 700, fontSize: '0.95rem', margin: '0.4rem 0 0.2rem 0' }}>
                Milestone Protection
              </div>
              <div style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: 1.4 }}>
                Pay only as working staging demos are inspected, tested, and approved.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2.5 Flagship Shipped Ecosystem (Proof of Systems Competence) */}
      <section id="ecosystem" style={{ padding: '5.5rem 0', backgroundColor: 'transparent', borderBottom: '1px solid #e2e8f0' }}>
        <div className="container-custom">
          <div style={{ textAlign: 'center', maxWidth: '820px', margin: '0 auto 3.5rem auto' }}>
            <div style={{ color: '#1d4ed8', fontSize: '0.82rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.8rem' }}>
              PROVEN PRODUCTION TRACK RECORD &bull; SHIPPED SOFTWARE
            </div>
            <h2 style={{ fontSize: 'clamp(2.2rem, 3.8vw, 3.2rem)', fontWeight: 900, color: '#0f172a', marginBottom: '1rem', letterSpacing: '-0.025em' }}>
              Flagship Production Systems
            </h2>
            <p style={{ color: '#475569', fontSize: '1.12rem', lineHeight: '1.6' }}>
              We don&apos;t just architect systems on paper—we build, ship, and operate them live. Explore our flagship software ecosystem built from scratch with zero-idle-cost cloud topologies, custom algorithms, and battle-tested reliability.
            </p>

            {/* Filter Pills */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1.8rem' }}>
              {[
                { id: 'all', label: 'All Flagship Systems' },
                { id: 'ai', label: 'AI & Audio Intelligence' },
                { id: 'saas', label: 'Multi-Tenant SaaS' },
                { id: 'stem', label: 'STEM & Algorithms' }
              ].map(f => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setEcosystemFilter(f.id)}
                  style={{
                    backgroundColor: ecosystemFilter === f.id ? '#1d4ed8' : '#ffffff',
                    color: ecosystemFilter === f.id ? '#ffffff' : '#475569',
                    border: ecosystemFilter === f.id ? '1px solid #1d4ed8' : '1px solid #cbd5e1',
                    borderRadius: '999px',
                    padding: '0.4rem 1rem',
                    fontSize: '0.82rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: ecosystemFilter === f.id ? '0 4px 12px rgba(29, 78, 216, 0.2)' : 'none'
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Cards Grid */}
          <div className="row g-4">
            {flagshipApps
              .filter(app => ecosystemFilter === 'all' || app.category === ecosystemFilter)
              .map(app => (
                <div key={app.id} className="col-lg-6 mb-4">
                  <div className="ecosystem-card">
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.4rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '16px',
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          padding: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.05)'
                        }}>
                          <img
                            src={app.logo}
                            alt={app.name}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        </div>
                        <div>
                          <h3 style={{ fontSize: '1.4rem', fontWeight: 900, color: '#0f172a', margin: '0 0 0.15rem 0' }}>
                            {app.name}
                          </h3>
                          <div style={{ fontSize: '0.82rem', color: '#1d4ed8', fontWeight: 700 }}>
                            {app.tagline}
                          </div>
                        </div>
                      </div>

                      <span style={{
                        backgroundColor: '#ecfdf5',
                        border: '1px solid #a7f3d0',
                        color: app.badgeColor,
                        fontSize: '0.72rem',
                        fontWeight: 800,
                        padding: '0.25rem 0.65rem',
                        borderRadius: '999px',
                        whiteSpace: 'nowrap'
                      }}>
                        {app.badge}
                      </span>
                    </div>

                    {/* Summary */}
                    <p style={{ color: '#475569', fontSize: '0.94rem', lineHeight: '1.65', marginBottom: '1.4rem', flexGrow: 1 }}>
                      {app.summary}
                    </p>

                    {/* Metrics Strip */}
                    <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.4rem' }}>
                      {app.metrics.map(m => (
                        <div key={m.label} className="ecosystem-metric-box">
                          <div style={{ fontSize: '1rem', fontWeight: 900, color: '#0f172a' }}>
                            {m.value}
                          </div>
                          <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: '0.2rem' }}>
                            {m.label}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Tech Stack Pills */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '1.6rem' }}>
                      {app.stack.map(tech => (
                        <span
                          key={tech}
                          style={{
                            backgroundColor: '#eff6ff',
                            color: '#1e40af',
                            border: '1px solid #dbeafe',
                            borderRadius: '6px',
                            padding: '0.2rem 0.55rem',
                            fontSize: '0.74rem',
                            fontWeight: 700
                          }}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>

                    {/* CTAs */}
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={() => setActiveAppModal(app)}
                        style={{
                          backgroundColor: '#f8fafc',
                          color: '#0f172a',
                          border: '1px solid #cbd5e1',
                          borderRadius: '10px',
                          padding: '0.65rem 1.1rem',
                          fontSize: '0.84rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.4rem',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#1d4ed8'; e.currentTarget.style.color = '#1d4ed8'; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#0f172a'; }}
                      >
                        <span>🔍</span> Inspect Architecture
                      </button>

                      <button
                        type="button"
                        onClick={() => handleCommissionApp(app)}
                        className="btn-primary-blue"
                        style={{
                          backgroundColor: '#1d4ed8',
                          color: '#ffffff',
                          borderRadius: '10px',
                          padding: '0.65rem 1.2rem',
                          fontSize: '0.84rem',
                          fontWeight: 800,
                          cursor: 'pointer',
                          flex: 1,
                          justifyContent: 'center'
                        }}
                      >
                        Commission Build Like This →
                      </button>

                      {app.route && (
                        app.isExternal ? (
                          <a
                            href={app.route}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#1d4ed8',
                              fontSize: '0.82rem',
                              fontWeight: 700,
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.2rem 0.5rem'
                            }}
                          >
                            Live Production App →
                          </a>
                        ) : (
                          <Link
                            to={app.route}
                            style={{
                              color: '#1d4ed8',
                              fontSize: '0.82rem',
                              fontWeight: 700,
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.2rem 0.5rem'
                            }}
                          >
                            Live App Experience →
                          </Link>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>

          {/* Institutional Trust Framework: Elite Systems Lab (No Agency Fluff) */}
          <div style={{
            marginTop: '3.5rem',
            backgroundColor: 'rgba(248, 250, 252, 0.75)',
            border: '1px solid #e2e8f0',
            borderRadius: '24px',
            padding: '2.5rem',
            boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.04)'
          }}>
            <div className="row align-items-center">
              <div className="col-lg-4 mb-4 mb-lg-0">
                <div style={{ fontSize: '0.78rem', color: '#1d4ed8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>
                  THE BOUTIQUE ADVANTAGE
                </div>
                <h3 style={{ fontSize: '1.6rem', fontWeight: 900, color: '#0f172a', lineHeight: 1.2, marginBottom: '0.8rem' }}>
                  Enterprise Rigor.<br />Zero Agency Overhead.
                </h3>
                <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>
                  Why enterprises, founders, and CTOs partner directly with our specialized digital workshop instead of traditional agencies.
                </p>
              </div>
              <div className="col-lg-8">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.2rem' }}>
                  <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.2rem' }}>
                    <div style={{ color: '#1d4ed8', fontSize: '1.2rem', marginBottom: '0.3rem' }}>🎯</div>
                    <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#0f172a', marginBottom: '0.25rem' }}>Direct Principal Architect</div>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.5 }}>
                      Zero junior handoffs or offshore roulette. Your system is architected, coded, and reviewed directly by a Principal Systems Architect.
                    </div>
                  </div>

                  <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.2rem' }}>
                    <div style={{ color: '#059669', fontSize: '1.2rem', marginBottom: '0.3rem' }}>⚡</div>
                    <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#0f172a', marginBottom: '0.25rem' }}>Battle-Tested In Production</div>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.5 }}>
                      We operate real production software with active users. Every architectural choice is verified against live traffic, not theory.
                    </div>
                  </div>

                  <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.2rem' }}>
                    <div style={{ color: '#d97706', fontSize: '1.2rem', marginBottom: '0.3rem' }}>🛡️</div>
                    <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#0f172a', marginBottom: '0.25rem' }}>Zero Counterparty Risk</div>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.5 }}>
                      40/30/30 milestone model with 100% IP &amp; Git repository handover from kickoff. Pay only after inspecting working software on live URLs.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Four Core Engineering Specializations */}
      <section id="services" style={{ padding: '5.5rem 0', backgroundColor: 'transparent', borderBottom: '1px solid #e2e8f0' }}>
        <div className="container-custom">
          <div style={{ textAlign: 'center', maxWidth: '750px', margin: '0 auto 4rem auto' }}>
            <div style={{ color: '#1d4ed8', fontSize: '0.82rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.8rem' }}>
              ENGINEERING PILLARS
            </div>
            <h2 style={{ fontSize: 'clamp(2.2rem, 3.8vw, 3.2rem)', fontWeight: 900, color: '#0f172a', marginBottom: '1rem', letterSpacing: '-0.025em' }}>
              Full-Stack Software, Mobile &amp; Cloud Systems
            </h2>
            <p style={{ color: '#475569', fontSize: '1.12rem', lineHeight: '1.6' }}>
              Built to high-throughput enterprise standards with zero third-party software taxes and contractually defined production SLAs.
            </p>
          </div>

          <div className="row">
            {/* Pillar 1 */}
            <div className="col-lg-6 mb-4">
              <div style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '20px',
                padding: '2.5rem',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.04)'
              }}>
                <div style={{ fontSize: '2.4rem', marginBottom: '1rem' }}>💻</div>
                <div style={{ color: '#1d4ed8', fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>
                  Web Applications &amp; Portals
                </div>
                <h3 style={{ color: '#0f172a', fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.8rem' }}>
                  Custom Web Apps &amp; Client Portals
                </h3>
                <p style={{ color: '#475569', fontSize: '0.96rem', lineHeight: '1.65', flexGrow: 1 }}>
                  Bespoke, end-to-end software development tailored for individuals and businesses. From responsive React/Next.js client portals and reservation ledgers to complex business workflows.
                </p>
                <div style={{ margin: '1.2rem 0' }}>
                  <span className="tech-tag" style={{ background: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' }}>React</span>
                  <span className="tech-tag" style={{ background: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' }}>Next.js</span>
                  <span className="tech-tag" style={{ background: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' }}>Node.js</span>
                  <span className="tech-tag" style={{ background: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' }}>TypeScript</span>
                  <span className="tech-tag" style={{ background: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' }}>Edge APIs</span>
                </div>
                <a href="#intake" onClick={() => { setSelectedPlatforms(['Responsive Web Application']); }} className="btn-primary-blue" style={{ justifyContent: 'center', backgroundColor: '#1d4ed8' }}>
                  Commission Web Application →
                </a>
              </div>
            </div>

            {/* Pillar 2 */}
            <div className="col-lg-6 mb-4">
              <div style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '20px',
                padding: '2.5rem',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.04)'
              }}>
                <div style={{ fontSize: '2.4rem', marginBottom: '1rem' }}>📱</div>
                <div style={{ color: '#1d4ed8', fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>
                  Mobile Engineering
                </div>
                <h3 style={{ color: '#0f172a', fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.8rem' }}>
                  Cross-Platform Mobile Utilities
                </h3>
                <p style={{ color: '#475569', fontSize: '0.96rem', lineHeight: '1.65', flexGrow: 1 }}>
                  High-velocity mobile applications for iOS and Android built on unified codebases. Native tactile feel, smooth gesture controls, offline-first local storage, and push notifications.
                </p>
                <div style={{ margin: '1.2rem 0' }}>
                  <span className="tech-tag" style={{ background: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' }}>iOS &amp; Android</span>
                  <span className="tech-tag" style={{ background: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' }}>Capacitor</span>
                  <span className="tech-tag" style={{ background: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' }}>PWA</span>
                  <span className="tech-tag" style={{ background: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' }}>Biometric Auth</span>
                </div>
                <a href="#intake" onClick={() => { setSelectedPlatforms(['Native iOS Application', 'Native Android Application']); }} className="btn-primary-blue" style={{ justifyContent: 'center', backgroundColor: '#1d4ed8' }}>
                  Commission Mobile App →
                </a>
              </div>
            </div>

            {/* Pillar 3 */}
            <div className="col-lg-6 mb-4">
              <div style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '20px',
                padding: '2.5rem',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.04)'
              }}>
                <div style={{ fontSize: '2.4rem', marginBottom: '1rem' }}>⚡</div>
                <div style={{ color: '#059669', fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>
                  Serverless Cloud &amp; Modern Databases
                </div>
                <h3 style={{ color: '#0f172a', fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.8rem' }}>
                  Cloud Architecture &amp; Automation
                </h3>
                <p style={{ color: '#475569', fontSize: '0.96rem', lineHeight: '1.65', flexGrow: 1 }}>
                  Zero-idle-cost architectures built on Serverless PostgreSQL, AWS DynamoDB, Lambda microservices, and Cloudflare edge. High-concurrency throughput with low-latency queries.
                </p>
                <div style={{ margin: '1.2rem 0' }}>
                  <span className="tech-tag" style={{ background: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0' }}>PostgreSQL</span>
                  <span className="tech-tag" style={{ background: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0' }}>AWS DynamoDB</span>
                  <span className="tech-tag" style={{ background: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0' }}>AWS Lambda</span>
                  <span className="tech-tag" style={{ background: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0' }}>Cloudflare</span>
                  <span className="tech-tag" style={{ background: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0' }}>CI/CD</span>
                </div>
                <a href="#intake" onClick={() => { setSelectedPlatforms(['Serverless Cloud & APIs']); }} className="btn-emerald" style={{ justifyContent: 'center' }}>
                  Commission Cloud Architecture →
                </a>
              </div>
            </div>

            {/* Pillar 4 */}
            <div className="col-lg-6 mb-4">
              <div style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '20px',
                padding: '2.5rem',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.04)'
              }}>
                <div style={{ fontSize: '2.4rem', marginBottom: '1rem' }}>🛡️</div>
                <div style={{ color: '#059669', fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>
                  Site Reliability &amp; SLAs
                </div>
                <h3 style={{ color: '#0f172a', fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.8rem' }}>
                  24/7 SLA Maintenance &amp; Bugfixes
                </h3>
                <p style={{ color: '#475569', fontSize: '0.96rem', lineHeight: '1.65', flexGrow: 1 }}>
                  Uptime SLAs available on maintenance retainers (terms defined in your SOW), continuous security dependency audits, performance monitoring, and proactive code maintenance for critical digital systems.
                </p>
                <div style={{ margin: '1.2rem 0' }}>
                  <span className="tech-tag" style={{ background: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0' }}>24/7 Monitoring</span>
                  <span className="tech-tag" style={{ background: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0' }}>Security Patches</span>
                  <span className="tech-tag" style={{ background: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0' }}>SLA Retainers</span>
                  <span className="tech-tag" style={{ background: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0' }}>Rapid Bugfixes</span>
                </div>
                <a href="#intake" onClick={() => { setSelectedPlatforms(['24/7 SLA Maintenance Retainer']); }} className="btn-emerald" style={{ justifyContent: 'center' }}>
                  Commission 24/7 Maintenance SLA →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Streamlined Build Intake Studio */}
      <section id="intake" style={{ padding: '5.5rem 0', backgroundColor: 'rgba(248, 250, 252, 0.72)', backdropFilter: 'blur(10px)', borderBottom: '1px solid #e2e8f0' }}>
        <div className="container-custom">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto 3.5rem auto' }}>
            <div style={{ color: '#1d4ed8', fontSize: '0.82rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
              START A BUILD &bull; ARCHITECTURE &amp; ESTIMATE
            </div>
            <h2 style={{ fontSize: 'clamp(2.1rem, 3.8vw, 3rem)', fontWeight: 900, color: '#0f172a', marginBottom: '0.8rem', letterSpacing: '-0.025em' }}>
              Tell Us What You Want to Build
            </h2>
            <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: '1.6' }}>
              Select your target deliverables, describe your idea, and Guru will personally prepare your bespoke architecture roadmap, database topology, and staged milestone estimate at zero upfront cost.
            </p>
          </div>

          <div style={{
            maxWidth: '1220px',
            margin: '0 auto',
            background: '#ffffff',
            borderRadius: '24px',
            border: '1px solid #e2e8f0',
            padding: '2.5rem',
            boxShadow: '0 24px 60px -15px rgba(15, 23, 42, 0.08)'
          }}>
            <form onSubmit={handleFinalIntakeSubmit}>
              <div className="row">
                {/* Left Column (col-lg-7): Configurator */}
                <div className="col-lg-7 pr-lg-4">
                  {/* Step 1: Target Deliverables */}
                  <div style={{ marginBottom: '1.8rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.6rem' }}>
                      <label style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a' }}>
                        1. Select Target Deliverables &amp; Platforms *
                      </label>
                      <span style={{ fontSize: '0.78rem', color: '#1d4ed8', fontWeight: 700 }}>
                        {selectedPlatforms.length} Selected (Multi-Platform)
                      </span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '0.85rem' }}>
                      {[
                        {
                          id: 'Responsive Web Application',
                          name: 'Responsive Web App',
                          badge: 'LIGHTHOUSE 100',
                          badgeBg: '#eff6ff',
                          badgeColor: '#1d4ed8',
                          desc: 'React / Next.js, Edge APIs, CSS micro-interactions, 100% responsive desktop & mobile.',
                          renderIcon: (color) => (
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                              <line x1="8" y1="21" x2="16" y2="21"></line>
                              <line x1="12" y1="17" x2="12" y2="21"></line>
                            </svg>
                          )
                        },
                        {
                          id: 'Native iOS Application',
                          name: 'Native iOS App',
                          badge: 'SWIFTUI / 5.0 ★',
                          badgeBg: '#f8fafc',
                          badgeColor: '#0f172a',
                          desc: 'Swift / Capacitor, Apple App Store ready, Biometrics, Apple Pay & native gesture control.',
                          renderIcon: (color) => (
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="5" y="2" width="14" height="20" rx="3"></rect>
                              <line x1="10" y1="5" x2="14" y2="5"></line>
                              <circle cx="12" cy="18" r="1"></circle>
                            </svg>
                          )
                        },
                        {
                          id: 'Native Android Application',
                          name: 'Native Android App',
                          badge: 'PLAY STORE READY',
                          badgeBg: '#ecfdf5',
                          badgeColor: '#059669',
                          desc: 'Kotlin / Capacitor, Google Play Store certified, Push notifications & offline-first sync.',
                          renderIcon: (color) => (
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="5" y="2" width="14" height="20" rx="2"></rect>
                              <line x1="12" y1="18" x2="12.01" y2="18"></line>
                              <path d="M16 2v2"></path>
                              <path d="M8 2v2"></path>
                            </svg>
                          )
                        },
                        {
                          id: 'Serverless Cloud & APIs',
                          name: 'Cloud Backend & DB',
                          badge: '$0 IDLE SERVERLESS',
                          badgeBg: '#ecfdf5',
                          badgeColor: '#059669',
                          desc: 'Serverless microservices, auto-scaling edge database & APIs architected for $0 idle baseline (cloud free-tier limits apply).',
                          renderIcon: (color) => (
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path>
                            </svg>
                          )
                        },
                        {
                          id: '24/7 SLA Maintenance Retainer',
                          name: '24/7 SLA Maintenance',
                          badge: 'UPTIME SLAS AVAILABLE',
                          badgeBg: '#fffbeb',
                          badgeColor: '#d97706',
                          desc: 'Continuous uptime heartbeat, sub-minute latency alerts, rapid bugfixes & automated daily backups.',
                          renderIcon: (color) => (
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                              <polyline points="9 12 11 14 15 10"></polyline>
                            </svg>
                          )
                        }
                      ].map((plat) => {
                        const isSelected = selectedPlatforms.includes(plat.id);
                        const isPreviewing = previewDeliverable === plat.id;
                        return (
                          <button
                            key={plat.id}
                            type="button"
                            onClick={() => togglePlatform(plat.id)}
                            onMouseEnter={() => setPreviewDeliverable(plat.id)}
                            className={`platform-card-pro ${isSelected ? 'selected' : ''}`}
                            style={{
                              borderColor: isSelected ? '#1d4ed8' : isPreviewing ? '#93c5fd' : '#e2e8f0',
                              backgroundColor: isSelected ? '#ffffff' : '#f8fafc',
                              boxShadow: isSelected
                                ? '0 10px 25px -5px rgba(29, 78, 216, 0.15), 0 0 0 1px #1d4ed8'
                                : isPreviewing
                                ? '0 6px 20px -4px rgba(29, 78, 216, 0.08)'
                                : '0 2px 8px rgba(15, 23, 42, 0.02)'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.65rem' }}>
                              <div style={{
                                width: '38px',
                                height: '38px',
                                borderRadius: '10px',
                                backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                                border: isSelected ? '1px solid #bfdbfe' : '1px solid #e2e8f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease'
                              }}>
                                {plat.renderIcon(isSelected ? '#1d4ed8' : '#475569')}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                {isPreviewing && (
                                  <span style={{ fontSize: '0.62rem', fontWeight: 800, color: '#1d4ed8', backgroundColor: '#eff6ff', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>
                                    PREVIEWING
                                  </span>
                                )}
                                <div style={{
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '6px',
                                  border: isSelected ? '2px solid #1d4ed8' : '1.5px solid #cbd5e1',
                                  backgroundColor: isSelected ? '#1d4ed8' : '#ffffff',
                                  color: '#ffffff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.75rem',
                                  fontWeight: 800,
                                  transition: 'all 0.2s ease'
                                }}>
                                  {isSelected ? '✓' : ''}
                                </div>
                              </div>
                            </div>

                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                                <span style={{ fontWeight: 800, fontSize: '0.9rem', color: isSelected ? '#1d4ed8' : '#0f172a' }}>
                                  {plat.name}
                                </span>
                                <span style={{
                                  fontSize: '0.65rem',
                                  fontWeight: 800,
                                  backgroundColor: plat.badgeBg,
                                  color: plat.badgeColor,
                                  padding: '0.15rem 0.45rem',
                                  borderRadius: '999px',
                                  letterSpacing: '0.04em'
                                }}>
                                  {plat.badge}
                                </span>
                              </div>
                              <p style={{ fontSize: '0.74rem', color: '#64748b', margin: 0, lineHeight: '1.4' }}>
                                {plat.desc}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Step 2: Project Title */}
                  <div style={{ marginBottom: '1.2rem' }}>
                    <label style={{ display: 'block', fontSize: '0.86rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.4rem' }}>
                      2. Project Name or Target Objective *
                    </label>
                    <input
                      id="intake-project-title"
                      type="text"
                      required
                      placeholder="e.g., Secure Multi-Tenant Client Reservation Portal"
                      value={projectTitle}
                      onChange={(e) => setProjectTitle(e.target.value)}
                      style={{
                        width: '100%',
                        backgroundColor: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '10px',
                        padding: '0.75rem 1rem',
                        color: '#0f172a',
                        fontSize: '0.95rem',
                        outline: 'none'
                      }}
                    />
                  </div>

                  {/* Step 3: Technical Requirements */}
                  <div style={{ marginBottom: '1.2rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <label style={{ fontSize: '0.86rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                        3. Description, Key Features or Integrations *
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {projectVision && projectVision.trim().length > 0 && (
                          <button
                            type="button"
                            onClick={handleCopySpec}
                            style={{
                              backgroundColor: copiedSpec ? '#ecfdf5' : '#f8fafc',
                              border: copiedSpec ? '1px solid #86efac' : '1px solid #cbd5e1',
                              color: copiedSpec ? '#059669' : '#475569',
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              padding: '0.25rem 0.65rem',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.35rem',
                              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                              transition: 'all 0.2s ease'
                            }}
                            title="Copy your project vision and requirements to clipboard"
                          >
                            {copiedSpec ? '✓ Copied Spec' : '📋 Copy Spec'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={handleAIEnhanceVision}
                          disabled={isAIEnhancing}
                          style={{
                            backgroundColor: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            color: '#1d4ed8',
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            padding: '0.25rem 0.75rem',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            boxShadow: '0 1px 3px rgba(29, 78, 216, 0.08)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#dbeafe'; }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                        >
                          {isAIEnhancing ? (
                            <><span>⏳</span> Formulating Architecture Spec...</>
                          ) : (
                            <><span>✨</span> AI Formalize &amp; Spec Enhancer</>
                          )}
                        </button>
                      </div>
                    </div>

                    {aiEnhancedBadge && (
                      <div style={{ backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', color: '#059669', fontSize: '0.78rem', fontWeight: 700, padding: '0.35rem 0.75rem', borderRadius: '6px', marginBottom: '0.5rem' }}>
                        ✓ Spec formulated with $0 idle architecture, user workflows &amp; cloud data tier!
                      </div>
                    )}

                    <textarea
                      required
                      rows={5}
                      placeholder="Describe what your system should do, expected users, integrations (e.g. Stripe, Auth0), target deadlines, or click '✨ AI Formalize' to auto-generate a spec..."
                      value={projectVision}
                      onChange={(e) => setProjectVision(e.target.value)}
                      style={{
                        width: '100%',
                        backgroundColor: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '10px',
                        padding: '0.75rem 1rem',
                        color: '#0f172a',
                        fontSize: '0.95rem',
                        outline: 'none',
                        resize: 'vertical',
                        lineHeight: '1.6'
                      }}
                    />
                  </div>

                  {/* Step 4: Target Timeline Preference */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.86rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.4rem' }}>
                      4. Target Timeline
                    </label>
                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                      {[
                        'Fast-Track (2 - 4 Weeks)',
                        'Standard (4 - 8 Weeks)',
                        'Flexible / Exploring Options'
                      ].map((time) => {
                        const isSelected = timelineTarget === time;
                        return (
                          <button
                            key={time}
                            type="button"
                            onClick={() => setTimelineTarget(time)}
                            style={{
                              backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                              border: isSelected ? '1.5px solid #1d4ed8' : '1px solid #cbd5e1',
                              color: isSelected ? '#1d4ed8' : '#334155',
                              padding: '0.45rem 0.9rem',
                              borderRadius: '8px',
                              fontSize: '0.82rem',
                              fontWeight: isSelected ? 700 : 500,
                              cursor: 'pointer',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            {isSelected ? '✓ ' : ''}{time}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Right Column (col-lg-5): Sticky Live Architecture Blueprint & Submission */}
                <div className="col-lg-5 pl-lg-4 mt-4 mt-lg-0">
                  <div className="scope-sticky-panel">
                    {/* Interactive Dual-Mode Switcher: Live Demo vs. Business ROI */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '1rem',
                      borderBottom: '1px solid #e2e8f0',
                      paddingBottom: '0.85rem',
                      gap: '0.5rem'
                    }}>
                      <div style={{ display: 'flex', gap: '0.35rem', backgroundColor: '#e2e8f0', padding: '0.2rem', borderRadius: '8px', flex: 1 }}>
                        <button
                          type="button"
                          id="btn-mode-simulator"
                          onClick={() => setRightPanelMode('simulator')}
                          style={{
                            flex: 1,
                            backgroundColor: rightPanelMode === 'simulator' ? '#ffffff' : 'transparent',
                            color: rightPanelMode === 'simulator' ? '#1d4ed8' : '#64748b',
                            border: 'none',
                            padding: '0.4rem 0.5rem',
                            borderRadius: '6px',
                            fontSize: '0.74rem',
                            fontWeight: 800,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.35rem',
                            boxShadow: rightPanelMode === 'simulator' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                            <line x1="8" y1="21" x2="16" y2="21"></line>
                            <line x1="12" y1="17" x2="12" y2="21"></line>
                          </svg>
                          <span>Live Simulator</span>
                        </button>
                        <button
                          type="button"
                          id="btn-mode-roi"
                          onClick={() => setRightPanelMode('roi')}
                          style={{
                            flex: 1,
                            backgroundColor: rightPanelMode === 'roi' ? '#ffffff' : 'transparent',
                            color: rightPanelMode === 'roi' ? '#059669' : '#64748b',
                            border: 'none',
                            padding: '0.4rem 0.5rem',
                            borderRadius: '6px',
                            fontSize: '0.74rem',
                            fontWeight: 800,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.35rem',
                            boxShadow: rightPanelMode === 'roi' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                            <polyline points="17 6 23 6 23 12"></polyline>
                          </svg>
                          <span>ROI &amp; Stats</span>
                        </button>
                      </div>

                      <span style={{
                        backgroundColor: '#ecfdf5',
                        border: '1px solid #a7f3d0',
                        color: '#059669',
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        padding: '0.25rem 0.5rem',
                        borderRadius: '999px',
                        letterSpacing: '0.02em',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}>
                        $0/mo Idle
                      </span>
                    </div>

                    {/* Mode 1: Interactive Device Simulator & Live Architecture Demo */}
                    {rightPanelMode === 'simulator' && (
                      <div style={{ marginBottom: '1.2rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            ● Previewing: {previewDeliverable.replace(' Application', '').replace(' Retainer', '')}
                          </span>
                          <span style={{ fontSize: '0.68rem', color: '#64748b' }}>
                            Interactive Demo
                          </span>
                        </div>

                        {/* Deliverable Specific Simulation Viewports */}
                        {previewDeliverable === 'Responsive Web Application' && (
                          <div className="real-device-card">
                            <div style={{ position: 'relative' }}>
                              <img
                                src="/assets/mockup_macbook_pro.jpg"
                                alt="MacBook Pro 16-inch Retina Responsive Web Dashboard"
                                className="real-device-img"
                                style={{ height: '210px', objectFit: 'cover', objectPosition: 'center 35%' }}
                              />
                              <div className="real-device-overlay-badge">
                                <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#10b981' }}></span>
                                <span>MacBook Pro 16" • Edge CDN</span>
                              </div>
                            </div>
                            <div className="real-device-footer">
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.65rem' }}>
                                <div>
                                  <div style={{ fontWeight: 800, fontSize: '0.86rem', color: '#0f172a' }}>
                                    Ultra-Fast Edge Architecture
                                  </div>
                                  <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                    React 19 + Next.js SSR / Sub-200ms TTFB
                                  </div>
                                </div>
                                <span style={{
                                  backgroundColor: '#eff6ff',
                                  color: '#1d4ed8',
                                  fontSize: '0.65rem',
                                  fontWeight: 800,
                                  padding: '0.15rem 0.45rem',
                                  borderRadius: '4px'
                                }}>
                                  LIGHTHOUSE 100
                                </span>
                              </div>

                              {/* Google Lighthouse Scores */}
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(4, 1fr)',
                                gap: '0.35rem',
                                backgroundColor: '#f8fafc',
                                padding: '0.55rem 0.4rem',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                textAlign: 'center'
                              }}>
                                <div>
                                  <div style={{ color: '#059669', fontWeight: 900, fontSize: '0.9rem' }}>100</div>
                                  <div style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 600 }}>Performance</div>
                                </div>
                                <div>
                                  <div style={{ color: '#059669', fontWeight: 900, fontSize: '0.9rem' }}>100</div>
                                  <div style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 600 }}>Accessibility</div>
                                </div>
                                <div>
                                  <div style={{ color: '#059669', fontWeight: 900, fontSize: '0.9rem' }}>100</div>
                                  <div style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 600 }}>Best Practices</div>
                                </div>
                                <div>
                                  <div style={{ color: '#059669', fontWeight: 900, fontSize: '0.9rem' }}>100</div>
                                  <div style={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 600 }}>SEO Rank</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {previewDeliverable === 'Native iOS Application' && (
                          <div className="real-device-card">
                            <div style={{ position: 'relative' }}>
                              <img
                                src="/assets/mockup_iphone_16_pro.jpg"
                                alt="iPhone 16 Pro Natural Titanium iOS Native App"
                                className="real-device-img"
                                style={{ height: '210px', objectFit: 'cover', objectPosition: 'center 45%' }}
                              />
                              <div className="real-device-overlay-badge">
                                <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#10b981' }}></span>
                                <span>iPhone 16 Pro • Titanium</span>
                              </div>
                            </div>
                            <div className="real-device-footer">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.45rem' }}>
                                <div style={{ fontWeight: 800, fontSize: '0.84rem', color: '#0f172a' }}>
                                  Apple App Store Certified
                                </div>
                                <span style={{ fontSize: '0.65rem', fontWeight: 800, color: '#1d4ed8', backgroundColor: '#eff6ff', padding: '0.15rem 0.45rem', borderRadius: '4px' }}>
                                  SWIFTUI / 5.0 ★
                                </span>
                              </div>
                              <div style={{ fontSize: '0.72rem', color: '#475569', lineHeight: '1.4', marginBottom: '0.5rem' }}>
                                Biometrics (FaceID/TouchID), Apple Pay checkout &amp; Dynamic Island live synchronization.
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', borderTop: '1px solid #f1f5f9', paddingTop: '0.45rem' }}>
                                <span style={{ color: '#64748b' }}>App Store Target:</span>
                                <strong style={{ color: '#059669' }}>100% Native Polish Approved</strong>
                              </div>
                            </div>
                          </div>
                        )}

                        {previewDeliverable === 'Native Android Application' && (
                          <div className="real-device-card">
                            <div style={{ position: 'relative' }}>
                              <img
                                src="/assets/mockup_android_phone.jpg"
                                alt="Flagship Android Obsidian Black Mobile App"
                                className="real-device-img"
                                style={{ height: '210px', objectFit: 'cover', objectPosition: 'center 40%' }}
                              />
                              <div className="real-device-overlay-badge">
                                <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#10b981' }}></span>
                                <span>Flagship Android • 5G</span>
                              </div>
                            </div>
                            <div className="real-device-footer">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.45rem' }}>
                                <div style={{ fontWeight: 800, fontSize: '0.84rem', color: '#0f172a' }}>
                                  Google Play Store Ready
                                </div>
                                <span style={{ fontSize: '0.65rem', fontWeight: 800, color: '#059669', backgroundColor: '#ecfdf5', padding: '0.15rem 0.45rem', borderRadius: '4px' }}>
                                  KOTLIN / PLAY CERTIFIED
                                </span>
                              </div>
                              <div style={{ fontSize: '0.72rem', color: '#475569', lineHeight: '1.4', marginBottom: '0.5rem' }}>
                                Offline-first SQLite sync, FCM push notifications, hardware camera scanner &amp; Material 3 UI.
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', borderTop: '1px solid #f1f5f9', paddingTop: '0.45rem' }}>
                                <span style={{ color: '#64748b' }}>Release Bundle:</span>
                                <strong style={{ color: '#059669' }}>AAB Production Bundle Ready</strong>
                              </div>
                            </div>
                          </div>
                        )}

                        {previewDeliverable === 'Serverless Cloud & APIs' && (
                          <div className="real-device-card">
                            <div style={{ position: 'relative' }}>
                              <img
                                src="/assets/mockup_cloud_serverless.jpg"
                                alt="Multi-Cloud Architecture Dashboard AWS Google Cloud Azure"
                                className="real-device-img"
                                style={{ height: '210px', objectFit: 'cover', objectPosition: 'center 40%' }}
                              />
                              <div className="real-device-overlay-badge">
                                <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#10b981' }}></span>
                                <span>Multi-Cloud • AWS / GCP / Azure</span>
                              </div>
                            </div>
                            <div className="real-device-footer">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.45rem' }}>
                                <div style={{ fontWeight: 800, fontSize: '0.84rem', color: '#0f172a' }}>
                                  Serverless Microservices &amp; DB
                                </div>
                                <span style={{ fontSize: '0.65rem', fontWeight: 800, color: '#059669', backgroundColor: '#ecfdf5', padding: '0.15rem 0.45rem', borderRadius: '4px' }}>
                                  $0.00/MO IDLE BASELINE
                                </span>
                              </div>
                              <div style={{ fontSize: '0.72rem', color: '#475569', lineHeight: '1.4', marginBottom: '0.5rem' }}>
                                Sub-15ms global edge CDN, 0ms cold-start serverless functions &amp; auto-scaling PostgreSQL.
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', borderTop: '1px solid #f1f5f9', paddingTop: '0.45rem' }}>
                                <span style={{ color: '#64748b' }}>Multi-Cloud Portability:</span>
                                <strong style={{ color: '#1d4ed8' }}>AWS + Google Cloud + Azure Ready</strong>
                              </div>
                            </div>
                          </div>
                        )}

                        {previewDeliverable === '24/7 SLA Maintenance Retainer' && (
                          <div className="real-device-card">
                            <div style={{ position: 'relative' }}>
                              <img
                                src="/assets/mockup_sla_heartbeat.jpg"
                                alt="24/7 SLA Operations Precision Clock and Heartbeat Telemetry"
                                className="real-device-img"
                                style={{ height: '210px', objectFit: 'cover', objectPosition: 'center 35%' }}
                              />
                              <div className="real-device-overlay-badge">
                                <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#10b981' }}></span>
                                <span>24/7 SLA • Ops Heartbeat</span>
                              </div>
                            </div>
                            <div className="real-device-footer">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.45rem' }}>
                                <div style={{ fontWeight: 800, fontSize: '0.84rem', color: '#0f172a' }}>
                                  Round-the-Clock Site Reliability
                                </div>
                                <span style={{ fontSize: '0.65rem', fontWeight: 800, color: '#d97706', backgroundColor: '#fffbeb', padding: '0.15rem 0.45rem', borderRadius: '4px' }}>
                                  UPTIME SLAS AVAILABLE (SOW DEFINED)
                                </span>
                              </div>
                              <div style={{ fontSize: '0.72rem', color: '#475569', lineHeight: '1.4', marginBottom: '0.5rem' }}>
                                Automated 60s health heartbeat, daily encrypted cloud backups &amp; sub-2hr incident remediation.
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', borderTop: '1px solid #f1f5f9', paddingTop: '0.45rem' }}>
                                <span style={{ color: '#64748b' }}>Operations Verification:</span>
                                <strong style={{ color: '#059669' }}>Continuous Live Shield Active</strong>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Mode 2: Business ROI, Growth Statistics & Financial Comparison */}
                    {rightPanelMode === 'roi' && (
                      <div style={{ marginBottom: '1.2rem' }}>
                        <div style={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #cbd5e1',
                          borderRadius: '12px',
                          padding: '1.1rem',
                          marginBottom: '0.8rem',
                          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)'
                        }}>
                          <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            📊 SaaS Subscription Tax vs Bespoke Asset
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', fontSize: '0.74rem' }}>
                            <div style={{ backgroundColor: '#fff1f2', border: '1px solid #fecdd3', padding: '0.65rem', borderRadius: '8px' }}>
                              <div style={{ fontWeight: 800, color: '#e11d48', marginBottom: '0.25rem' }}>Third-Party SaaS Rentals</div>
                              <div style={{ color: '#881337', lineHeight: '1.4' }}>
                                • <strong>$450 – $1,200/mo</strong> recurring<br />
                                • $5,400+ burned annually<br />
                                • 1.5% – 3% transaction cut<br />
                                • 0% Code Ownership (Locked)
                              </div>
                            </div>

                            <div style={{ backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', padding: '0.65rem', borderRadius: '8px' }}>
                              <div style={{ fontWeight: 800, color: '#059669', marginBottom: '0.25rem' }}>Guru's Workshop LLC</div>
                              <div style={{ color: '#064e3b', lineHeight: '1.4' }}>
                                • <strong>$0.00/mo Idle Hosting</strong><br />
                                • 0 recurring subscription tax<br />
                                • 0% transaction fee cut<br />
                                • <strong>100% Client IP Ownership</strong>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Grounded Growth Metrics */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gap: '0.5rem',
                          textAlign: 'center'
                        }}>
                          <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.65rem 0.35rem' }}>
                            <div style={{ fontSize: '1rem', fontWeight: 900, color: '#1d4ed8' }}>+42%</div>
                            <div style={{ fontSize: '0.64rem', color: '#475569', fontWeight: 700, marginTop: '0.15rem' }}>Conversion Uplift</div>
                            <div style={{ fontSize: '0.58rem', color: '#94a3b8' }}>Sub-second speed</div>
                          </div>
                          <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.65rem 0.35rem' }}>
                            <div style={{ fontSize: '1rem', fontWeight: 900, color: '#059669' }}>15+ hrs</div>
                            <div style={{ fontSize: '0.64rem', color: '#475569', fontWeight: 700, marginTop: '0.15rem' }}>Saved / Week</div>
                            <div style={{ fontSize: '0.58rem', color: '#94a3b8' }}>Zero manual entry</div>
                          </div>
                          <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.65rem 0.35rem' }}>
                            <div style={{ fontSize: '1rem', fontWeight: 900, color: '#d97706' }}>&lt; 7 wks</div>
                            <div style={{ fontSize: '0.64rem', color: '#475569', fontWeight: 700, marginTop: '0.15rem' }}>Payback Period</div>
                            <div style={{ fontSize: '0.58rem', color: '#94a3b8' }}>Eliminates SaaS</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Scope Summary Box */}
                    <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '1.1rem', marginBottom: '1.1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.76rem', color: '#64748b', fontWeight: 700 }}>Configured Architecture</span>
                        <span style={{ fontSize: '0.74rem', fontWeight: 800, color: '#1d4ed8' }}>
                          {selectedPlatforms.length} Deliverable{selectedPlatforms.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.75rem' }}>
                        {selectedPlatforms.map(p => (
                          <span
                            key={p}
                            onClick={() => setPreviewDeliverable(p)}
                            style={{
                              backgroundColor: previewDeliverable === p ? '#eff6ff' : '#f8fafc',
                              border: previewDeliverable === p ? '1px solid #93c5fd' : '1px solid #e2e8f0',
                              color: previewDeliverable === p ? '#1d4ed8' : '#475569',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              padding: '0.2rem 0.5rem',
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            {p.replace(' Application', '').replace(' Serverless', '')}
                          </span>
                        ))}
                      </div>

                      <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '0.65rem', display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                        <span style={{ color: '#64748b' }}>Cloud Economics:</span>
                        <strong style={{ color: '#059669' }}>
                          $0.00/mo Idle (Permanent Free Tier)
                        </strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginTop: '0.35rem' }}>
                        <span style={{ color: '#64748b' }}>Code &amp; IP:</span>
                        <strong style={{ color: '#0f172a' }}>
                          100% Client Ownership
                        </strong>
                      </div>
                    </div>

                    {/* 3-Stage Milestone Protection Model */}
                    <div style={{ marginBottom: '1.2rem' }}>
                      <div style={{ fontSize: '0.76rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.45rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        🛡️ 3-Stage Milestone Protection
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', fontSize: '0.76rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#334155' }}>
                          <span style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', fontWeight: 800, padding: '0.12rem 0.4rem', borderRadius: '4px' }}>Stage 1 (40%)</span>
                          <span>Architecture kickoff &amp; data model</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#334155' }}>
                          <span style={{ backgroundColor: '#ecfdf5', color: '#059669', fontWeight: 800, padding: '0.12rem 0.4rem', borderRadius: '4px' }}>Stage 2 (30%)</span>
                          <span>Functional staging demo review</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#334155' }}>
                          <span style={{ backgroundColor: '#f8fafc', border: '1px solid #cbd5e1', color: '#0f172a', fontWeight: 800, padding: '0.12rem 0.4rem', borderRadius: '4px' }}>Stage 3 (30%)</span>
                          <span>Production DNS &amp; 100% IP handover</span>
                        </div>
                      </div>
                    </div>

                    {/* Account Authentication & Cross-Device Access */}
                    {currentUser ? (
                      <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #86efac', padding: '0.85rem 1rem', borderRadius: '10px', marginBottom: '1.2rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div>
                            <div style={{ fontSize: '0.72rem', color: '#16a34a', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              ✓ Authenticated Client Account
                            </div>
                            <div style={{ fontSize: '0.95rem', color: '#0f172a', fontWeight: 700, marginTop: '0.15rem' }}>
                              {currentUser.name}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                              {currentUser.email}
                            </div>
                          </div>
                          <span style={{ fontSize: '1.4rem' }}>🛡️</span>
                        </div>
                        <div style={{ fontSize: '0.74rem', color: '#15803d', marginTop: '0.45rem', borderTop: '1px solid #dcfce7', paddingTop: '0.45rem' }}>
                          This project proposal will be linked directly to your dashboard across all devices.
                        </div>
                      </div>
                    ) : (
                      <div style={{ marginBottom: '1.2rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
                          <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            Account &amp; Project Access
                          </span>
                          <div style={{ display: 'flex', gap: '0.3rem', backgroundColor: '#e2e8f0', padding: '0.15rem', borderRadius: '6px' }}>
                            <button
                              type="button"
                              onClick={() => { setIntakeAuthMode('signup'); setIntakeAuthError(''); }}
                              style={{
                                border: 'none',
                                background: intakeAuthMode === 'signup' ? '#ffffff' : 'transparent',
                                color: intakeAuthMode === 'signup' ? '#1d4ed8' : '#64748b',
                                fontSize: '0.74rem',
                                fontWeight: 700,
                                padding: '0.2rem 0.6rem',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              New Account
                            </button>
                            <button
                              type="button"
                              onClick={() => { setIntakeAuthMode('signin'); setIntakeAuthError(''); }}
                              style={{
                                border: 'none',
                                background: intakeAuthMode === 'signin' ? '#ffffff' : 'transparent',
                                color: intakeAuthMode === 'signin' ? '#1d4ed8' : '#64748b',
                                fontSize: '0.74rem',
                                fontWeight: 700,
                                padding: '0.2rem 0.6rem',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              Sign In
                            </button>
                          </div>
                        </div>

                        {intakeAuthError && (
                          <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '0.8rem', fontWeight: 600, padding: '0.5rem 0.75rem', borderRadius: '8px', marginBottom: '0.8rem' }}>
                            ⚠️ {intakeAuthError}
                          </div>
                        )}

                        {intakeAuthMode === 'signup' && (
                          <div style={{ marginBottom: '0.7rem' }}>
                            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.25rem' }}>
                              Your Full Name *
                            </label>
                            <input
                              type="text"
                              required
                              placeholder="Alex Morgan"
                              value={clientName}
                              onChange={(e) => setClientName(e.target.value)}
                              style={{
                                width: '100%',
                                backgroundColor: '#ffffff',
                                border: '1px solid #cbd5e1',
                                borderRadius: '8px',
                                padding: '0.6rem 0.85rem',
                                color: '#0f172a',
                                fontSize: '0.88rem',
                                outline: 'none'
                              }}
                            />
                          </div>
                        )}

                        <div style={{ marginBottom: '0.7rem' }}>
                          <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.25rem' }}>
                            Work Email Address *
                          </label>
                          <input
                            type="email"
                            required
                            placeholder="alex@company.com"
                            value={clientEmail}
                            onChange={(e) => setClientEmail(e.target.value)}
                            style={{
                              width: '100%',
                              backgroundColor: '#ffffff',
                              border: '1px solid #cbd5e1',
                              borderRadius: '8px',
                              padding: '0.6rem 0.85rem',
                              color: '#0f172a',
                              fontSize: '0.88rem',
                              outline: 'none'
                            }}
                          />
                        </div>

                        <div style={{ marginBottom: '0.7rem' }}>
                          <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.25rem' }}>
                            {intakeAuthMode === 'signup' ? 'Create Account Password *' : 'Account Password *'}
                          </label>
                          <input
                            type="password"
                            required
                            placeholder="Minimum 6 characters"
                            value={clientPassword}
                            onChange={(e) => setClientPassword(e.target.value)}
                            style={{
                              width: '100%',
                              backgroundColor: '#ffffff',
                              border: '1px solid #cbd5e1',
                              borderRadius: '8px',
                              padding: '0.6rem 0.85rem',
                              color: '#0f172a',
                              fontSize: '0.88rem',
                              outline: 'none'
                            }}
                          />
                          <span style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '0.25rem', display: 'block' }}>
                            {intakeAuthMode === 'signup'
                              ? '🔒 Secures your proposal so you can track it across any laptop, phone, or browser.'
                              : '🔑 Enter your password to link this RFP to your existing profile.'}
                          </span>
                        </div>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary-blue"
                      style={{
                        width: '100%',
                        justifyContent: 'center',
                        padding: '0.9rem',
                        fontSize: '0.95rem',
                        fontWeight: 800,
                        borderRadius: '10px',
                        backgroundColor: '#1d4ed8',
                        color: '#ffffff',
                        cursor: 'pointer'
                      }}
                    >
                      {isSubmitting
                        ? 'Securing Scope & Generating RFP...'
                        : currentUser
                          ? 'Submit Architecture Scope & Estimate \u2192'
                          : intakeAuthMode === 'signup'
                            ? 'Create Account & Request Scope \u2192'
                            : 'Sign In & Request Scope \u2192'}
                    </button>
                    <div style={{ textAlign: 'center', fontSize: '0.74rem', color: '#64748b', marginTop: '0.7rem' }}>
                      Zero upfront commitment. Mutual NDA protected.
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* 4.5 Enterprise Procurement Assurance & Risk-Reversal FAQ */}
      <section style={{ padding: '5rem 0', backgroundColor: 'transparent', borderBottom: '1px solid #e2e8f0' }}>
        <div className="container-custom">
          <div style={{ textAlign: 'center', maxWidth: '720px', margin: '0 auto 3.5rem auto' }}>
            <div style={{ color: '#1d4ed8', fontSize: '0.82rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
              CLIENT COMMITMENTS &amp; PROTECTIONS
            </div>
            <h2 style={{ fontSize: 'clamp(1.9rem, 3.2vw, 2.6rem)', fontWeight: 900, color: '#0f172a', marginBottom: '0.8rem' }}>
              Enterprise Procurement Assurance
            </h2>
            <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: '1.6' }}>
              Institutional transparency designed to eliminate counterparty risk for startup founders, enterprise directors, and technical leaders.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem' }}>
            <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '2rem', boxShadow: '0 2px 8px rgba(15, 23, 42, 0.03)' }}>
              <div style={{ fontSize: '2.2rem', marginBottom: '0.8rem' }}>📦</div>
              <h4 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.4rem' }}>
                Code &amp; IP Handover
              </h4>
              <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>
                You own full rights to all delivered software repositories, database schemas, and CI/CD pipelines upon SOW completion. Zero proprietary licenses, no SaaS markups, and zero vendor lock-in.
              </p>
            </div>

            <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '2rem', boxShadow: '0 2px 8px rgba(15, 23, 42, 0.03)' }}>
              <div style={{ fontSize: '2.2rem', marginBottom: '0.8rem' }}>☁️</div>
              <h4 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.4rem' }}>
                $0/Month Permanent Baseline
              </h4>
              <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>
                Architected with Serverless Databases (PostgreSQL / DynamoDB), AWS Lambda microservices, and Cloudflare edge. Your infrastructure incurs $0.00 while idle (AWS/GCP free tiers apply), scaling dynamically with real traffic.
              </p>
            </div>

            <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '2rem', boxShadow: '0 2px 8px rgba(15, 23, 42, 0.03)' }}>
              <div style={{ fontSize: '2.2rem', marginBottom: '0.8rem' }}>🛡️</div>
              <h4 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.4rem' }}>
                3-Stage Milestone Protection
              </h4>
              <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>
                Never pay 100% upfront. Kickoff with 40% deposit, release 30% only after testing your live functional staging demo, and release final 30% upon production DNS cutover.
              </p>
            </div>

            <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '2rem', boxShadow: '0 2px 8px rgba(15, 23, 42, 0.03)' }}>
              <div style={{ fontSize: '2.2rem', marginBottom: '0.8rem' }}>🔒</div>
              <h4 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.4rem' }}>
                Mutual NDA &amp; SLA Guardrails
              </h4>
              <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>
                All architecture specs and product visions are strictly protected under mutual NDA prior to discovery. Uptime SLAs available on maintenance retainers (terms defined in your SOW).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Dynamic Rolling Poster for Services Provided */}
      <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(8px)', padding: '2rem 0' }}>
        <ServiceRollingPoster />
      </div>

      {/* 6. Enterprise Confidentiality & Security Banner */}
      <section style={{ backgroundColor: 'rgba(248, 250, 252, 0.75)', backdropFilter: 'blur(10px)', borderTop: '1px solid #e2e8f0', padding: '3.5rem 0' }}>
        <div className="container-custom">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>🔒</span>
                <h3 style={{ color: '#0f172a', margin: 0, fontSize: '1.35rem', fontWeight: 800 }}>
                  Enterprise Confidentiality &amp; IP Protection
                </h3>
              </div>
              <p style={{ color: '#475569', margin: 0, fontSize: '0.96rem', lineHeight: '1.6' }}>
                All build parameters and client specifications are protected under mutual NDA. We never introduce proprietary licensing taxes, vector DB lock-in, or managed CMS markups. Delivered directly by the <strong>Principal Systems Architect at Guru&apos;s Workshop LLC</strong>.
              </p>
            </div>
            <div className="col-lg-4 text-lg-right mt-3 mt-lg-0">
              <Link to="/rfp" className="btn-primary-blue" style={{ background: '#1d4ed8' }}>
                Submit Formal Enterprise RFP →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Built by Guru's Workshop — Studio & Principal Systems Architect Section */}
      <section style={{
        backgroundColor: '#ffffff',
        borderTop: '1px solid #e2e8f0',
        padding: '5rem 0',
        position: 'relative'
      }}>
        <div className="container-custom">
          <div style={{
            maxWidth: '1060px',
            margin: '0 auto',
            backgroundColor: '#f8fafc',
            border: '1.5px solid #e2e8f0',
            borderRadius: '24px',
            padding: '3rem',
            boxShadow: '0 20px 45px -15px rgba(15, 23, 42, 0.06)'
          }}>
            <div className="row align-items-center">
              <div className="col-lg-4 text-center mb-4 mb-lg-0">
                <div style={{
                  width: '210px',
                  height: '210px',
                  borderRadius: '24px',
                  margin: '0 auto',
                  overflow: 'hidden',
                  border: '3px solid #ffffff',
                  boxShadow: '0 12px 30px rgba(15, 23, 42, 0.12)',
                  backgroundColor: '#0f172a',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}>
                  <div style={{
                    width: '76px',
                    height: '76px',
                    borderRadius: '18px',
                    background: 'linear-gradient(135deg, #1d4ed8 0%, #0f172a 100%)',
                    border: '1.5px solid rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ffffff',
                    fontWeight: 900,
                    fontSize: '2rem',
                    letterSpacing: '-0.03em',
                    boxShadow: '0 8px 20px rgba(29, 78, 216, 0.35)'
                  }}>
                    GW
                  </div>
                  <div style={{ color: '#ffffff', fontWeight: 800, fontSize: '0.95rem', marginTop: '0.9rem', letterSpacing: '0.02em' }}>
                    Guru&apos;s Workshop LLC
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: '0.74rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '0.2rem' }}>
                    Principal Practice
                  </div>
                </div>
                <div style={{ marginTop: '1rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', fontSize: '0.76rem', fontWeight: 700, padding: '0.25rem 0.75rem', borderRadius: '999px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#059669' }}></span>
                  Solo Practice &bull; Direct Architect Engagement
                </div>
              </div>

              <div className="col-lg-8">
                <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
                  THE STUDIO ADVANTAGE
                </div>
                <h2 style={{ fontSize: '2.2rem', fontWeight: 900, color: '#0f172a', margin: '0 0 0.5rem 0', letterSpacing: '-0.025em' }}>
                  Built by Guru&apos;s Workshop
                </h2>
                <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#334155', marginBottom: '1.2rem' }}>
                  Principal Systems Architect — 20+ Years Enterprise Systems Engineering
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', fontSize: '0.78rem', fontWeight: 700, padding: '0.3rem 0.8rem', borderRadius: '8px', marginBottom: '1.4rem' }}>
                  <span>⭐</span> 20+ Years Shipping Enterprise Software &bull; Fortune 50 Retail &bull; Global Financial Systems &bull; Cloud Scale
                </div>
                <p style={{ color: '#475569', fontSize: '0.98rem', lineHeight: '1.7', marginBottom: '1.2rem' }}>
                  Guru&apos;s Workshop LLC is an independent, senior-operated software engineering and cloud architecture practice. With over two decades of track record architecting and shipping mission-critical systems for high-throughput retail, enterprise financial services, and cloud platforms, every client engagement is executed with uncompromising engineering discipline.
                </p>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: '1.7', marginBottom: '1.8rem' }}>
                  Because this is an autonomous, boutique consultancy, you partner directly with a veteran Principal Architect from initial discovery through production deployment. There are no junior handoffs, no offshore translation layers, and no agency overhead—just direct architectural ownership, transparent deliverables, and production software engineered to last.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                  <Link
                    to="/rfp"
                    className="btn-primary-blue"
                    style={{
                      backgroundColor: '#1d4ed8',
                      color: '#ffffff',
                      textDecoration: 'none',
                      padding: '0.75rem 1.4rem',
                      borderRadius: '10px',
                      fontSize: '0.92rem',
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <span>📋</span> Request Architectural RFP Consultation →
                  </Link>
                  <a
                    href="mailto:contact@gurusworkshop.com?subject=Direct%20Architectural%20Inquiry%20%E2%80%94%20Guru%27s%20Workshop"
                    style={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #cbd5e1',
                      color: '#0f172a',
                      textDecoration: 'none',
                      padding: '0.75rem 1.3rem',
                      borderRadius: '10px',
                      fontSize: '0.92rem',
                      fontWeight: 700
                    }}
                  >
                    Direct Email Inquiry
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive System Architecture Inspector Modal */}
      {activeAppModal && (
        <div className="arch-modal-overlay" onClick={() => setActiveAppModal(null)}>
          <div className="arch-modal-content" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <img src={activeAppModal.logo} alt={activeAppModal.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.45rem', fontWeight: 900, color: '#0f172a', margin: 0 }}>
                    {activeAppModal.name} &bull; Architecture Blueprint
                  </h3>
                  <div style={{ fontSize: '0.84rem', color: '#1d4ed8', fontWeight: 700 }}>
                    {activeAppModal.tagline}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveAppModal(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  color: '#64748b',
                  cursor: 'pointer',
                  padding: '0.2rem 0.5rem',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            {/* System Topology Diagram */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>
                {'// SYSTEM TOPOLOGY & DATA PIPELINE'}
              </div>
              <div style={{
                backgroundColor: '#0f172a',
                color: '#f8fafc',
                borderRadius: '12px',
                padding: '1.2rem',
                fontFamily: 'monospace',
                fontSize: '0.86rem',
                lineHeight: '1.7',
                overflowX: 'auto'
              }}>
                <div style={{ color: '#38bdf8' }}>{activeAppModal.architectureDetails.topology}</div>
              </div>
            </div>

            {/* Cloud Cost & Security Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '12px', padding: '1.2rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.3rem' }}>
                  Cloud Cost Baseline
                </div>
                <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#065f46', marginBottom: '0.3rem' }}>
                  {activeAppModal.architectureDetails.cloudCost}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#047857' }}>
                  Zero recurring SaaS subscription overhead. Auto-scales up only on live load.
                </div>
              </div>

              <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '1.2rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.3rem' }}>
                  Security &amp; Data Governance
                </div>
                <div style={{ fontSize: '0.82rem', color: '#1e40af', lineHeight: '1.5' }}>
                  {activeAppModal.architectureDetails.security}
                </div>
              </div>
            </div>

            {/* Key Innovations */}
            <div style={{ marginBottom: '1.8rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>
                Key Engineering Innovations
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#475569', fontSize: '0.88rem', lineHeight: '1.7' }}>
                {activeAppModal.architectureDetails.keyInnovations.map((inn, idx) => (
                  <li key={idx} style={{ marginBottom: '0.4rem' }}>{inn}</li>
                ))}
              </ul>
            </div>

            {/* Modal CTAs */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.8rem', borderTop: '1px solid #e2e8f0', paddingTop: '1.2rem' }}>
              <button
                type="button"
                onClick={() => setActiveAppModal(null)}
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '10px',
                  padding: '0.7rem 1.2rem',
                  fontSize: '0.88rem',
                  fontWeight: 700,
                  color: '#475569',
                  cursor: 'pointer'
                }}
              >
                Close Blueprint
              </button>
              <button
                type="button"
                onClick={() => handleCommissionApp(activeAppModal)}
                className="btn-primary-blue"
                style={{
                  backgroundColor: '#1d4ed8',
                  color: '#ffffff',
                  borderRadius: '10px',
                  padding: '0.7rem 1.4rem',
                  fontSize: '0.88rem',
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
              >
                Commission System Like This →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkshopHome;
